import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from '../utils/toast';

const WS_BASE_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:8000';

/**
 * Unified WebSocket hook with channel-based subscriptions.
 * Replaces old useWebSocket and useKnowledgeWebSocket hooks.
 *
 * @returns {Object} WebSocket state and methods
 */
export function useWebSocket() {
  const navigate = useNavigate();
  const { logout, token, user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const pingIntervalRef = useRef(null);
  const messageHandlersRef = useRef({});  // channel/type -> handler
  const logoutRef = useRef(logout);
  const navigateRef = useRef(navigate);
  const hasConnectedRef = useRef(false);
  const isInitialConnectionRef = useRef(true); // Track if this is the initial connection attempt

  const reconnectDelay = 5000; // Retry every 5 seconds
  const pingInterval = 30000;

  // Keep refs up to date
  useEffect(() => {
    logoutRef.current = logout;
    navigateRef.current = navigate;
  }, [logout, navigate]);

  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      } catch (err) {
        console.error('[WebSocket] Failed to send ping:', err);
      }
    }
  }, []);

  const connect = useCallback(() => {
    // Prevent duplicate connections
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    try {
      // Use token from AuthContext (already validated)
      const authToken = token || sessionStorage.getItem('token') || localStorage.getItem('token');
      if (!authToken) {
        console.error('[WebSocket] No authentication token found');
        setError('Not authenticated');
        return;
      }

      hasConnectedRef.current = true;

      const encodedToken = encodeURIComponent(authToken);
      const wsUrl = `${WS_BASE_URL}/ws?token=${encodedToken}`;

      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        const attemptCount = reconnectAttemptsRef.current;
        console.log('[WebSocket] Connected' + (attemptCount > 0 ? ` (after ${attemptCount} ${attemptCount === 1 ? 'attempt' : 'attempts'})` : ''));
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
        isInitialConnectionRef.current = false; // Mark that we've successfully connected once

        // Show success toast if reconnected after failure (attemptCount > 0 means we reconnected)
        if (attemptCount > 0) {
          toast.success('Reconnected to server');
        }

        // Start ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        pingIntervalRef.current = setInterval(sendPing, pingInterval);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const messageType = data.type;

          // Built-in handlers for system messages (process BEFORE registered handlers)
          if (messageType === 'pong') {
            // Keepalive response
          } else if (messageType === 'subscribed') {
            console.log(`[WebSocket] Subscribed to ${data.channel}`);
          } else if (messageType === 'unsubscribed') {
            console.log(`[WebSocket] Unsubscribed from ${data.channel}`);
          } else if (messageType === 'auth_error') {
            // Session expired or invalid CEK - logout and redirect
            console.log('[WebSocket] Authentication error - session expired');
            toast.error(data.message || 'Session expired. Please login again.');
            hasConnectedRef.current = false;
            reconnectAttemptsRef.current = 0;
            logoutRef.current();
            navigateRef.current('/', { replace: true });
            return; // Don't process further handlers
          } else if (messageType === 'error') {
            console.error('[WebSocket] Server error:', data.message);
            // Only show persistent errors in UI, not transient connection issues
            // Use toast for user-facing error notifications
            // Skip toast for special error codes that have custom handling (like no_products)
            const skipToastForErrorCodes = ['no_products'];
            if (!isInitialConnectionRef.current && data.message && !data.message.includes('No connection configured') && !skipToastForErrorCodes.includes(data.error_code)) {
              toast.error(data.message);
            }
            // Continue to registered handlers - don't return early!
          }

          // Call registered handlers for this message type (including 'error')
          const handlers = messageHandlersRef.current[messageType];
          if (handlers && handlers.length > 0) {
            handlers.forEach(handler => {
              try {
                handler(data);
              } catch (err) {
                console.error(`[WebSocket] Handler error for ${messageType}:`, err);
              }
            });
          }

        } catch (err) {
          console.error('[WebSocket] Failed to parse message:', err);
        }
      };

      wsRef.current.onerror = (event) => {
        console.error('[WebSocket] Error:', event);
        // Don't show errors during initial connection attempt
        // They'll be handled by onclose if connection fails
      };

      wsRef.current.onclose = (event) => {
        console.log('[WebSocket] Closed with code:', event.code, 'reason:', event.reason, 'wasClean:', event.wasClean);
        setIsConnected(false);

        // Stop ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Check for authentication failure (403, invalid token, etc.)
        // Error codes: 1002 = protocol error, 1003 = unsupported data, 1006 = abnormal closure (403 rejection),
        // 1008 = policy violation - all indicate auth failures that shouldn't retry
        if (event.code === 1002 || event.code === 1003 || event.code === 1006 || event.code === 1008) {
          console.log('[WebSocket] Authentication failed (code:', event.code, ') - logging out');
          toast.error('Your session has expired. Please log in again.');
          hasConnectedRef.current = false; // Reset connection flag for re-login
          reconnectAttemptsRef.current = 0; // Reset reconnect attempts
          logoutRef.current();
          navigateRef.current('/', { replace: true });
          return;
        }

        // Don't reconnect if we don't have valid auth
        if (!token || !user) {
          console.log('[WebSocket] Not authenticated - stopping reconnection attempts');
          hasConnectedRef.current = false;
          reconnectAttemptsRef.current = 0;
          return;
        }

        // Always attempt to reconnect (never give up) - but only if authenticated
        reconnectAttemptsRef.current += 1;
        console.log(`[WebSocket] Reconnecting in ${reconnectDelay / 1000}s... (Attempt ${reconnectAttemptsRef.current})`);

        // Only show error toast after multiple failed attempts (not during initial connection)
        if (!isInitialConnectionRef.current && reconnectAttemptsRef.current >= 3) {
          toast.error('Connection lost. Attempting to reconnect...');
        }

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, reconnectDelay);
      };
    } catch (err) {
      console.error('[WebSocket] Failed to create connection:', err);
      // Don't set error state during initial connection, use toast for persistent issues
      if (!isInitialConnectionRef.current) {
        toast.error('Failed to establish connection');
      }
    }
  }, [token, sendPing]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
    setError(null);
    hasConnectedRef.current = false;
    isInitialConnectionRef.current = true; // Reset for next connection
  }, []);

  const send = useCallback((message) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('[WebSocket] Cannot send - not connected');
      return false;
    }

    try {
      wsRef.current.send(JSON.stringify(message));
      return true;
    } catch (err) {
      console.error('[WebSocket] Failed to send message:', err);
      return false;
    }
  }, []);

  const subscribe = useCallback((channel) => {
    return send({ type: 'subscribe', channel });
  }, [send]);

  const unsubscribe = useCallback((channel) => {
    return send({ type: 'unsubscribe', channel });
  }, [send]);

  const on = useCallback((messageType, handler) => {
    if (!messageHandlersRef.current[messageType]) {
      messageHandlersRef.current[messageType] = [];
    }
    messageHandlersRef.current[messageType].push(handler);

    // Return cleanup function
    return () => {
      const handlers = messageHandlersRef.current[messageType];
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      }
    };
  }, []);

  const off = useCallback((messageType, handler) => {
    const handlers = messageHandlersRef.current[messageType];
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }, []);

  // Connect when user is authenticated
  useEffect(() => {
    if (token && user && !hasConnectedRef.current) {
      console.log('[WebSocket] User authenticated - connecting');
      connect();
    } else if (!token || !user) {
      console.log('[WebSocket] User not authenticated - skipping connection');
      // Disconnect if we were connected and user logged out
      if (hasConnectedRef.current) {
        disconnect();
      }
    }

    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user]); // Connect/disconnect based on auth state

  // Listen for login events to reconnect after re-authentication
  useEffect(() => {
    const handleLogin = () => {
      // The main useEffect will handle connection via token/user state changes
      // This is just for logging
      console.log('[WebSocket] Login event detected');
    };

    // Listen for custom login event
    window.addEventListener('userLoggedIn', handleLogin);

    // Also listen for storage events (cross-tab login)
    const handleStorageChange = (e) => {
      if (e.key === 'token' && e.newValue) {
        console.log('[WebSocket] Token detected in another tab');
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('userLoggedIn', handleLogin);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return {
    isConnected,
    error,
    isInitialConnection: isInitialConnectionRef.current,
    connect,
    disconnect,
    send,
    subscribe,
    unsubscribe,
    on,
    off,
  };
}
